import type { FC, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'
import type { Category, ProductSummary } from '@/types'

import { useMemo, useState } from 'react'
import { useRouter } from 'next/router'
import { ChevronDown, Search, SearchX } from 'lucide-react'
import { cn } from '@/lib/utils'
import { pluralize } from '@/lib/format'
import { getAllProducts, getCategories } from '@/lib/catalog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import Container from '@/components/generals/container'
import WaveLines from '@/components/generals/wave-lines'
import ProductCard from '@/components/shop/product-card'

type SortValue = 'featured' | 'price-asc' | 'price-desc' | 'name'

interface ShopPageProps {
    categories: Array<Category>
    products: Array<ProductSummary>
    failed: boolean
}

const SORT_OPTIONS: Array<{ value: SortValue, label: string }> = [
    { value: 'featured', label: "Staff picks first" },
    { value: 'price-asc', label: "Price, low to high" },
    { value: 'price-desc', label: "Price, high to low" },
    { value: 'name', label: "Name, A to Z" }
]

const pillClass = (active: boolean): string => cn(
    'inline-flex shrink-0 items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition-colors focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none',
    active
        ? 'border-primary bg-primary text-primary-foreground'
        : 'border-border bg-card text-foreground hover:border-primary/40'
)

const countClass = (active: boolean): string => cn(
    'rounded-full px-1.5 text-xs tabular-nums',
    active ? 'bg-primary-foreground/15' : 'bg-muted text-muted-foreground'
)

export const getServerSideProps: GetServerSideProps<ShopPageProps> = async () => {
    try {
        const [categories, products] = await Promise.all([getCategories(), getAllProducts()])

        return { props: { categories, products, failed: false } }
    }
    catch (error) {
        console.error('Shop catalog failed to load:', error instanceof Error ? error.message : error)

        return { props: { categories: [], products: [], failed: true } }
    }
}

const Shop: FC<ShopPageProps> = ({ categories, products, failed }): ReactNode => {
    const router = useRouter()
    const [search, setSearch] = useState('')
    const [sort, setSort] = useState<SortValue>('featured')
    const [hideSoldOut, setHideSoldOut] = useState(false)

    const requested = typeof router.query.category === 'string' ? router.query.category : ''
    const activeCategory = categories.find(category => category.slug === requested) ?? null

    const visible = useMemo(() => {
        const term = search.trim().toLowerCase()

        const filtered = products.filter(product => {
            if (activeCategory && product.category_slug !== activeCategory.slug) return false
            if (hideSoldOut && !product.in_stock) return false
            if (!term) return true

            return [product.name, product.tagline, product.category_name]
                .some(value => value?.toLowerCase().includes(term))
        })

        const sorted = [...filtered]

        if (sort === 'price-asc') sorted.sort((a, b) => a.price - b.price)
        if (sort === 'price-desc') sorted.sort((a, b) => b.price - a.price)
        if (sort === 'name') sorted.sort((a, b) => a.name.localeCompare(b.name))
        if (sort === 'featured') sorted.sort((a, b) => Number(b.in_stock) - Number(a.in_stock) || Number(b.featured) - Number(a.featured))

        return sorted
    }, [products, activeCategory, hideSoldOut, search, sort])

    const selectCategory = (slug: string | null) => {
        router.replace(
            { pathname: '/shop', query: slug ? { category: slug } : {} },
            undefined,
            { shallow: true, scroll: false }
        )
    }

    const resetFilters = () => {
        setSearch('')
        setHideSoldOut(false)
        selectCategory(null)
    }

    return (
        <Layout>
            <Seo
                title='Shop'
                description="Browse rods, reels, lures, fly gear and line from a local tackle shop. Reserve online and pay when you pick up in store."
            />

            <section aria-labelledby='shop-title' className='relative overflow-hidden border-b border-border bg-secondary/60'>
                <WaveLines className='absolute inset-x-0 bottom-0 h-32 w-full text-primary/10' lines={5} />

                <Container className='relative py-12 sm:py-16'>
                    <p className='text-xs font-bold tracking-[0.18em] text-tide uppercase'>
                        The tackle wall
                    </p>

                    <h1 id='shop-title' className='mt-3 text-4xl font-extrabold tracking-tight sm:text-5xl'>
                        {activeCategory
                            ? (
                                <span data-mdlfy-cms={`categories:${activeCategory.id}:name`}>
                                    {activeCategory.name}
                                </span>
                            )
                            : 'All the gear'}
                    </h1>

                    {activeCategory?.description
                        ? (
                            <p
                                data-mdlfy-cms={`categories:${activeCategory.id}:description`}
                                className='mt-4 max-w-2xl text-lg leading-relaxed text-muted-foreground'
                            >
                                {activeCategory.description}
                            </p>
                        )
                        : (
                            <p className='mt-4 max-w-2xl text-lg leading-relaxed text-muted-foreground'>
                                Everything on the wall, ready to reserve. Pick it up at the counter and pay when you collect.
                            </p>
                        )}
                </Container>
            </section>

            <Container className='py-8 sm:py-10'>
                <div className='flex flex-col gap-5 xl:flex-row xl:items-center xl:justify-between'>
                    <div
                        role='group'
                        aria-label='Filter by category'
                        className='no-scrollbar -mx-5 flex gap-2 overflow-x-auto px-5 pb-1 sm:mx-0 sm:flex-wrap sm:overflow-visible sm:px-0 sm:pb-0'
                    >
                        <button
                            type='button'
                            aria-pressed={!activeCategory}
                            onClick={() => selectCategory(null)}
                            className={pillClass(!activeCategory)}
                        >
                            All gear

                            <span className={countClass(!activeCategory)}>
                                {products.length}
                            </span>
                        </button>

                        {categories.map(category => {
                            const active = activeCategory?.id === category.id

                            return (
                                <button
                                    key={category.id}
                                    type='button'
                                    aria-pressed={active}
                                    onClick={() => selectCategory(category.slug)}
                                    className={pillClass(active)}
                                >
                                    <span data-mdlfy-cms={`categories:${category.id}:name`}>
                                        {category.name}
                                    </span>

                                    <span className={countClass(active)}>
                                        {category.product_count}
                                    </span>
                                </button>
                            )
                        })}
                    </div>

                    <div className='flex flex-col gap-3 sm:flex-row sm:items-center'>
                        <div className='relative sm:w-64'>
                            <label htmlFor='shop-search' className='sr-only'>
                                Search the shop
                            </label>

                            <Search className='pointer-events-none absolute top-1/2 left-3.5 size-4 -translate-y-1/2 text-muted-foreground' />

                            <Input
                                id='shop-search'
                                type='search'
                                value={search}
                                onChange={event => setSearch(event.target.value)}
                                placeholder='Search lures, reels, line'
                                className='rounded-full pl-10'
                            />
                        </div>

                        <div className='relative'>
                            <label htmlFor='shop-sort' className='sr-only'>
                                Sort products
                            </label>

                            <select
                                id='shop-sort'
                                value={sort}
                                onChange={event => setSort(event.target.value as SortValue)}
                                className='h-11 w-full appearance-none rounded-full border border-input bg-card pr-10 pl-4 text-sm font-semibold outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/40 sm:w-auto'
                            >
                                {SORT_OPTIONS.map(option => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>

                            <ChevronDown className='pointer-events-none absolute top-1/2 right-3.5 size-4 -translate-y-1/2 text-muted-foreground' />
                        </div>

                        <label className='flex h-11 cursor-pointer items-center gap-2.5 px-1 text-sm font-semibold select-none'>
                            <input
                                type='checkbox'
                                checked={hideSoldOut}
                                onChange={event => setHideSoldOut(event.target.checked)}
                                className='size-4 accent-primary'
                            />
                            Hide sold out
                        </label>
                    </div>
                </div>

                <p aria-live='polite' className='mt-8 text-sm text-muted-foreground'>
                    Showing {visible.length} {pluralize(visible.length, 'product')}
                </p>

                {failed && (
                    <div className='mt-6 rounded-3xl bg-card p-8 text-center ring-1 ring-border'>
                        <p className='font-heading text-xl font-bold'>
                            The shelves are being restocked
                        </p>

                        <p className='mt-2 text-muted-foreground'>
                            We could not load the products just now. Please try again in a moment, or give the shop a call.
                        </p>
                    </div>
                )}

                {!failed && visible.length > 0 && (
                    <div className='mt-4 grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'>
                        {visible.map(product => (
                            <ProductCard key={product.id} product={product} />
                        ))}
                    </div>
                )}

                {!failed && visible.length === 0 && (
                    <div className='mt-4 flex flex-col items-center rounded-3xl bg-card px-6 py-16 text-center ring-1 ring-border'>
                        <span className='flex size-14 items-center justify-center rounded-full bg-secondary text-primary'>
                            <SearchX className='size-6' />
                        </span>

                        <p className='mt-4 font-heading text-xl font-bold'>
                            Nothing on the wall matches that
                        </p>

                        <p className='mt-2 max-w-md text-muted-foreground'>
                            Try a different search or category. If you are after something specific, ask us and we will check the back room.
                        </p>

                        <Button variant='outline' className='mt-6' onClick={resetFilters}>
                            Clear filters
                        </Button>
                    </div>
                )}
            </Container>
        </Layout>
    )
}

export default Shop
